import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AppHeader } from "@/components/app/AppHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { useAuth, DEPARTMENT_LABELS, type Department } from "@/lib/auth-context";
import {
  computeSalary, fmtINR, getPaidLeavesUsed, listSalariesForMonth, upsertSalary,
  type SalaryCalc,
} from "@/lib/salary";
import { cn } from "@/lib/utils";
import { BulkProcessSalaryModal } from "@/components/hr/BulkProcessSalaryModal";
import { todayIstIso } from "@/lib/tz";

export const Route = createFileRoute("/_app/_hr/hr/payroll")({
  component: PayrollPage,
  head: () => ({ meta: [{ title: "Salary & Payroll — HR" }] }),
});

interface Profile {
  id: string;
  full_name: string;
  employee_id: string | null;
  department: string;
  status: string;
  monthly_salary: number;
}

function monthOptions(): string[] {
  const out: string[] = [];
  const d = new Date();
  for (let i = 0; i < 12; i++) {
    const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push(`${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}

function PayrollPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [month, setMonth] = useState<string>(monthOptions()[0]);
  const [openEmp, setOpenEmp] = useState<Profile | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const { data: employees = [] } = useQuery({
    queryKey: ["payroll-employees"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, employee_id, department, status")
        .eq("status", "active")
        .order("full_name");
      if (error) throw error;
      const rows = (data ?? []) as Array<Omit<Profile, "monthly_salary">>;
      const ids = rows.map((r) => r.id);
      let salaryById: Record<string, number> = {};
      if (ids.length) {
        const { data: priv } = await supabase
          .from("profiles_private")
          .select("id, monthly_salary")
          .in("id", ids);
        salaryById = Object.fromEntries(
          (priv ?? []).map((p) => [p.id as string, Number(p.monthly_salary ?? 0)]),
        );
      }
      return rows.map((r) => ({ ...r, monthly_salary: salaryById[r.id] ?? 0 })) as Profile[];
    },
  });

  const { data: salaries = [] } = useQuery({
    queryKey: ["salaries", month],
    queryFn: () => listSalariesForMonth(month),
  });

  const salaryByEmp = useMemo(() => {
    const m: Record<string, SalaryCalc> = {};
    salaries.forEach((s) => { m[s.employee_id] = s; });
    return m;
  }, [salaries]);

  const stats = useMemo(() => {
    const total = salaries.reduce((s, x) => s + Number(x.net_salary), 0);
    const paidCount = salaries.filter((x) => x.payment_status === "paid").length;
    const pendingCount = salaries.filter((x) => x.payment_status === "pending").length;
    const totalBonus = salaries.reduce((s, x) => s + Number(x.bonus), 0);
    return { total, paidCount, pendingCount, totalBonus };
  }, [salaries]);

  const monthLabel = new Date(month + "-01").toLocaleString("en-US", { month: "long", year: "numeric" });

  return (
    <>
      <AppHeader subtitle={`${monthLabel} · ${employees.length} employees · ${fmtINR(stats.total)} total payout`} />
      <div className="p-6 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 flex-1">
            <StatCard label="Total Payroll" value={fmtINR(stats.total)} />
            <StatCard label="Processed" value={`${stats.paidCount} / ${employees.length}`} />
            <StatCard label="Pending Payment" value={String(stats.pendingCount)} />
            <StatCard label="Total Bonus Paid" value={fmtINR(stats.totalBonus)} />
          </div>
        </div>

        <div className="flex items-center justify-between gap-2">
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="h-9 rounded-md border border-border bg-white px-3 text-xs"
          >
            {monthOptions().map((m) => (
              <option key={m} value={m}>{new Date(m + "-01").toLocaleString("en-US", { month: "long", year: "numeric" })}</option>
            ))}
          </select>
          <Button className="bg-black text-white hover:bg-black/90" onClick={() => setBulkOpen(true)}>+ Bulk Process</Button>
        </div>

        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Emp ID</TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>Dept</TableHead>
                  <TableHead className="text-right">Basic</TableHead>
                  <TableHead className="text-center">Days</TableHead>
                  <TableHead className="text-center">½ Days</TableHead>
                  <TableHead className="text-center">Paid Leaves</TableHead>
                  <TableHead className="text-center">Eff. Days</TableHead>
                  <TableHead className="text-right">Gross</TableHead>
                  <TableHead className="text-right">Bonus</TableHead>
                  <TableHead className="text-right">Deduct.</TableHead>
                  <TableHead className="text-right">Net</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {employees.map((emp) => {
                  const s = salaryByEmp[emp.id];
                  return (
                    <TableRow key={emp.id}>
                      <TableCell className="font-mono text-xs">{emp.employee_id ?? "—"}</TableCell>
                      <TableCell className="font-medium text-xs">{emp.full_name}</TableCell>
                      <TableCell className="text-xs">{DEPARTMENT_LABELS[emp.department as Department] ?? emp.department}</TableCell>
                      <TableCell className="text-right text-xs">{fmtINR(Number(emp.monthly_salary))}</TableCell>
                      <TableCell className="text-center text-xs">{s ? Number(s.days_present).toFixed(1) : "—"}</TableCell>
                      <TableCell className="text-center text-xs">{s ? s.half_days_taken : "—"}</TableCell>
                      <TableCell className="text-center text-xs">{s ? Number(s.paid_leaves_used).toFixed(1) : "—"}</TableCell>
                      <TableCell className="text-center text-xs">{s ? Number(s.effective_days_paid).toFixed(1) : "—"}</TableCell>
                      <TableCell className="text-right text-xs">{s ? fmtINR(Number(s.gross_salary)) : "—"}</TableCell>
                      <TableCell className="text-right text-xs">{s ? fmtINR(Number(s.bonus)) : "—"}</TableCell>
                      <TableCell className="text-right text-xs">{s ? fmtINR(Number(s.deductions)) : "—"}</TableCell>
                      <TableCell className="text-right text-xs font-semibold">{s ? fmtINR(Number(s.net_salary)) : "—"}</TableCell>
                      <TableCell>
                        {s?.payment_status === "paid" ? (
                          <Badge className="bg-green-600 hover:bg-green-700">Paid</Badge>
                        ) : (
                          <Badge className="bg-amber-500 hover:bg-amber-600">Pending</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setOpenEmp(emp)}>
                          Process →
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {openEmp && (
        <ProcessSalaryModal
          employee={openEmp}
          month={month}
          existing={salaryByEmp[openEmp.id]}
          calculatedBy={user!.id}
          onClose={() => setOpenEmp(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["salaries", month] });
            setOpenEmp(null);
          }}
        />
      )}

      <BulkProcessSalaryModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        month={month}
        employees={employees}
        calculatedBy={user!.id}
      />
    </>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <Card><CardContent className="p-4">
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </CardContent></Card>
  );
}

function ProcessSalaryModal({
  employee, month, existing, calculatedBy, onClose, onSaved,
}: {
  employee: Profile;
  month: string;
  existing: SalaryCalc | undefined;
  calculatedBy: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [daysPresent, setDaysPresent] = useState<string>(String(existing?.days_present ?? 30));
  const [halfDays, setHalfDays] = useState<string>(String(existing?.half_days_taken ?? 0));
  const [bonus, setBonus] = useState<string>(String(existing?.bonus ?? 0));
  const [deductions, setDeductions] = useState<string>(String(existing?.deductions ?? 0));
  const [notes, setNotes] = useState<string>(existing?.notes ?? "");
  const [paid, setPaid] = useState<boolean>(existing?.payment_status === "paid");
  const [paidOn, setPaidOn] = useState<string>(existing?.paid_on ?? todayIstIso());
  const [paymentRef, setPaymentRef] = useState<string>(existing?.payment_reference ?? "");

  const { data: paidLeavesUsed = 0 } = useQuery({
    queryKey: ["paid-leaves-used", employee.id, month],
    queryFn: () => getPaidLeavesUsed(employee.id, month),
  });

  const computed = useMemo(() => computeSalary({
    basic_salary: Number(employee.monthly_salary),
    days_present: Number(daysPresent) || 0,
    half_days_taken: Number(halfDays) || 0,
    paid_leaves_used: paidLeavesUsed,
    bonus: Number(bonus) || 0,
    deductions: Number(deductions) || 0,
  }), [employee.monthly_salary, daysPresent, halfDays, paidLeavesUsed, bonus, deductions]);

  const save = useMutation({
    mutationFn: () => upsertSalary({
      employee_id: employee.id,
      month,
      month_days: 30,
      days_present: Number(daysPresent) || 0,
      half_days_taken: Number(halfDays) || 0,
      paid_leaves_used: paidLeavesUsed,
      effective_days_paid: computed.effective_days_paid,
      basic_salary: Number(employee.monthly_salary),
      per_day_rate: computed.per_day_rate,
      gross_salary: computed.gross_salary,
      bonus: Number(bonus) || 0,
      deductions: Number(deductions) || 0,
      net_salary: computed.net_salary,
      payment_status: paid ? "paid" : "pending",
      paid_on: paid ? paidOn : null,
      payment_reference: paid ? (paymentRef || null) : null,
      notes: notes || null,
      calculated_by: calculatedBy,
    }),
    onSuccess: () => { toast.success(`Salary processed for ${employee.full_name}`); onSaved(); },
    onError: (e: Error) => toast.error(e.message),
  });

  const Pill = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) => (
    <button type="button" onClick={onClick} className={cn(
      "h-8 rounded-full px-4 text-xs font-medium",
      active ? "bg-black text-white" : "bg-white text-foreground border border-border hover:bg-muted",
    )}>{children}</button>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Calculate Salary — {employee.full_name} · #{employee.employee_id ?? "—"}</DialogTitle>
          <p className="text-xs text-muted-foreground">{new Date(month + "-01").toLocaleString("en-US", { month: "long", year: "numeric" })}</p>
        </DialogHeader>

        <div className="space-y-4">
          <Section title="Attendance Input">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Days Present *</Label>
                <Input type="number" min={0} max={30} value={daysPresent} onChange={(e) => setDaysPresent(e.target.value)} />
                <p className="text-[10px] text-muted-foreground mt-1">From attendance machine. Full days only.</p>
              </div>
              <div>
                <Label className="text-xs">Half Days Taken</Label>
                <Input type="number" min={0} max={30} value={halfDays} onChange={(e) => setHalfDays(e.target.value)} />
                <p className="text-[10px] text-muted-foreground mt-1">4 half days = 2 paid days</p>
              </div>
            </div>
          </Section>

          <Section title="Paid Leave (auto)">
            <KV label="Paid Leaves Used (approved)" value={`${paidLeavesUsed.toFixed(1)} days`} />
            <KV label="Half Day Equivalent" value={`${computed.half_day_equivalent.toFixed(1)} days`} />
            <KV label="Total Paid Allowance" value={`${computed.total_paid_allowance.toFixed(1)} days (capped at quota)`} />
          </Section>

          <Section title="Salary Breakdown">
            <KV label="Basic Salary" value={fmtINR(Number(employee.monthly_salary))} />
            <KV label="Per Day Rate" value={fmtINR(computed.per_day_rate)} />
            <KV label="Days Present" value={String(Number(daysPresent) || 0)} />
            <KV label="Paid Leave Allowance" value={`${computed.total_paid_allowance.toFixed(1)} days`} />
            <KV label="Effective Days Paid" value={`${computed.effective_days_paid.toFixed(1)} days`} />
            <div className="border-t my-2" />
            <KV label="Gross Salary" value={fmtINR(computed.gross_salary)} bold />
          </Section>

          <Section title="Adjustments">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Bonus (₹)</Label><Input type="number" value={bonus} onChange={(e) => setBonus(e.target.value)} /></div>
              <div><Label className="text-xs">Deductions (₹)</Label><Input type="number" value={deductions} onChange={(e) => setDeductions(e.target.value)} /></div>
            </div>
            <div className="mt-3"><Label className="text-xs">Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
          </Section>

          <div className="rounded-lg border-2 border-violet-200 bg-violet-50 p-4 space-y-1 text-sm">
            <div className="flex justify-between"><span>Gross:</span><span>{fmtINR(computed.gross_salary)}</span></div>
            <div className="flex justify-between text-green-700"><span>+ Bonus:</span><span>{fmtINR(Number(bonus) || 0)}</span></div>
            <div className="flex justify-between text-red-700"><span>- Deductions:</span><span>{fmtINR(Number(deductions) || 0)}</span></div>
            <div className="border-t border-violet-300 my-2" />
            <div className="flex justify-between font-bold text-base text-violet-800"><span>NET PAYABLE:</span><span>{fmtINR(computed.net_salary)}</span></div>
          </div>

          <Section title="Payment Details">
            <div className="flex gap-2">
              <Pill active={!paid} onClick={() => setPaid(false)}>Pending</Pill>
              <Pill active={paid} onClick={() => setPaid(true)}>Mark as Paid</Pill>
            </div>
            {paid && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Paid On</Label><Input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} /></div>
                <div><Label className="text-xs">Payment Reference</Label><Input value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} placeholder="UPI / bank ref" /></div>
              </div>
            )}
          </Section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-black text-white hover:bg-black/90" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? "Saving..." : (paid ? "Save & Mark Paid →" : "Save Draft")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">{title}</div>
      <div className="rounded-md border border-border p-3 bg-muted/20">{children}</div>
    </div>
  );
}

function KV({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className={cn("flex justify-between text-sm py-1", bold && "font-bold")}>
      <span className="text-muted-foreground">{label}:</span>
      <span>{value}</span>
    </div>
  );
}
